import { useEffect, useState } from "react"
import Table from "@/common/components/Table"
import { PageTitleNavs } from "@/common/components/page-title-navs"
import { LOGISTIC_INVOICE, RECEIVED_INVOICES } from "@/common/constants"
import useQuery from "@/hooks/useQuery"
import styled from "styled-components"
import { getInvoiceTableData, invoiceTableHeaders, invoiceTableKeys, InvoiceFilterOptions } from "./subs/data"
import { KeyValueObj } from "@/common/types"
import { useNavigate, useSearchParams } from "react-router-dom"
import useAuth from "@/hooks/useContexts"
import { UserRole } from "@/types/enum"
import { toParamsFormat } from "@/common/utils/general"

const InvoicesWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;

  @media (max-width: ${({ theme }) => theme.screen.mobile}) {
    padding: 2rem 0.5rem;
  }
`



enum InvoiceURL {
  Sent = "sent",
  Received = "received",
}

export enum IInvoiceType {
  MEDEX = "medex",
  LOGISTIC = "logistic",
}

export interface IInvoicesProps {}

export function Invoices(_: IInvoicesProps) {
  const user = useAuth()
  const [searchParams] = useSearchParams()
  const invoiceTypeParam = searchParams.get("page")

  // pagination (if backend supports, same as inventory)
  // page/perPage are currently static but included in query params
  const [tablePage] = useState(1)
  const [perPage] = useState(25)

  // keep filter parameters in one object so url updates automatically
  const [filterParams, setFilterParams] = useState<KeyValueObj>({})

  const url: InvoiceURL =
    user?.auth?.user?.user_company_type === UserRole.Manufacturer ? InvoiceURL.Sent : InvoiceURL.Received

  const buildApiUrl = () => {
    const baseUrl =
      invoiceTypeParam === IInvoiceType.MEDEX
        ? RECEIVED_INVOICES + url
        : LOGISTIC_INVOICE + "/recipient"

    // always include pagination
    const merged = {
      ...filterParams,
      page: tablePage,
      per_page: perPage,
    }

    const params = toParamsFormat(merged)
    return params ? `${baseUrl}?${params}` : baseUrl
  }

  const {
    data: receivedInvoices,
    loading: loadingInvoices,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useQuery(buildApiUrl())

  const [invoiceTableData, setInvoiceTableData] = useState<KeyValueObj[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    setInvoiceTableData(getInvoiceTableData(receivedInvoices))
  }, [receivedInvoices])

  // handlers used by Table component
  function handleSearch(value: string) {
    // search resets other filters (same pattern as inventory table)
    setFilterParams({ search_term: value || undefined })
  }

  function handleFilter(values: KeyValueObj) {
    // resets filters (search term handled separately)
    setFilterParams(values)
  }

  return (
    <InvoicesWrapper>
      <PageTitleNavs
        title="Invoices"
        backLink="#"
        bcrumbs={[{ title: "Invoices", link: "#" }]}
      />

      <div className="table-wrapper">
        <Table
          tableHeaders={invoiceTableHeaders}
          tableKeys={invoiceTableKeys}
          tableData={invoiceTableData}
          showDropDown
          loading={loadingInvoices}
          errorLoading={invoicesError?.length > 0}
          onRetry={() => refetchInvoices(buildApiUrl())}
          dropDownOptions={[
            {
              action: (row) => navigate(`${row?.id}?from=${invoiceTypeParam}`),
              label: "View",
            },
          ]}
          filterInputs={InvoiceFilterOptions}
          handleFilter={handleFilter}
          handleSearch={handleSearch}
        />
      </div>
    </InvoicesWrapper>
  )
}
